import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  appwriteSessionGateway,
  type PendingCode,
  type RegisterInput,
  type SessionGateway,
} from '../../services/appwrite/session-gateway';
import { track } from '../../services/activity/activity-client';
import type { SessionProfile } from '../../types/session';
import { reportClientError } from '../../services/appwrite/report-client-error';
import {
  ACCESS_VIEW_STORAGE_KEY,
  actualAccessLevel,
  allowedAccessView,
  profileForAccessView,
  storedAccessView,
  type AccessLevel,
  type AccessView,
} from './session-access-view';

export type SessionState =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; profile: SessionProfile }
  | { status: 'unavailable'; message: string };

interface SessionContextValue {
  state: SessionState;
  refresh(): Promise<void>;
  signIn(): Promise<void>;
  signInWithPassword(identifier: string, password: string): Promise<void>;
  register(input: RegisterInput): Promise<PendingCode>;
  resendCode(pending: PendingCode): Promise<void>;
  /** Valida el código y refresca la sesión: sirve para registro y recuperación. */
  verifyCode(pending: PendingCode, code: string): Promise<void>;
  requestPasswordReset(identifier: string): Promise<PendingCode>;
  confirmPasswordReset(newPassword: string): Promise<void>;
  updateNotificationSoundsEnabled(enabled: boolean): void;
  updateNotificationEmailsEnabled(enabled: boolean): void;
  updateNotificationToastsEnabled(enabled: boolean): void;
  actualAccessLevel: AccessLevel;
  accessView: AccessView;
  setAccessView(view: Exclude<AccessView, null>): void;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps extends PropsWithChildren {
  gateway?: SessionGateway;
}

// Una comprobación de sesión no puede dejar bloqueada toda la aplicación si
// una Function síncrona quedó colgada en Appwrite. Las rutas públicas siguen
// siendo utilizables mientras se comprueba la sesión y las protegidas reciben
// un error accionable en vez de quedar eternamente en «Cargando…».
const SESSION_CHECK_TIMEOUT_MS = 2_500;

async function loadSessionWithTimeout(gateway: SessionGateway): Promise<Awaited<ReturnType<SessionGateway['load']>>> {
  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      gateway.load(),
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('La comprobación de sesión tardó demasiado.')), SESSION_CHECK_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

function readableError(error: unknown): string {
  if (error instanceof Error && /failed to fetch|network|load failed/i.test(error.message)) {
    return 'Parece que la conexión se cortó justo ahora. Probemos de nuevo.';
  }
  return 'Algo se interrumpió de nuestro lado, nada grave. Probemos de nuevo.';
}

export function SessionProvider({
  children,
  gateway = appwriteSessionGateway,
}: SessionProviderProps) {
  const [state, setState] = useState<SessionState>({ status: 'checking' });
  const actualProfile = useRef<SessionProfile | null>(null);
  const [accessView, setStoredAccessView] = useState<AccessView>(null);
  const [realAccessLevel, setRealAccessLevel] = useState<AccessLevel>('user');

  const refresh = useCallback(async () => {
    setState({ status: 'checking' });
    try {
      const profile = await loadSessionWithTimeout(gateway);
      if (profile) {
        const view = storedAccessView(profile);
        actualProfile.current = profile;
        setStoredAccessView(view);
        setRealAccessLevel(actualAccessLevel(profile));
        setState({ status: 'authenticated', profile: profileForAccessView(profile, view) });
      } else {
        actualProfile.current = null;
        setState({ status: 'anonymous' });
      }
    } catch (error) {
      console.error('[Rol Sensorial] No se pudo recuperar la sesión.', error);
      reportClientError({
        source: 'web-session',
        operation: 'session-check',
        message: 'No se pudo comprobar la sesión del navegador.',
        details: error instanceof Error ? error.message : String(error),
      });
      setState({ status: 'unavailable', message: readableError(error) });
    }
  }, [gateway]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async () => {
    await gateway.signInWithGoogle();
  }, [gateway]);

  const signInWithPassword = useCallback(
    async (identifier: string, password: string) => {
      try {
        await gateway.signInWithPassword(identifier, password);
      } catch (error) {
        track('login_failed', { method: 'password' });
        throw error;
      }
      track('login_success', { method: 'password' });
      await refresh();
    },
    [gateway, refresh],
  );

  const register = useCallback(
    (input: RegisterInput) => gateway.register(input),
    [gateway],
  );

  const resendCode = useCallback(
    (pending: PendingCode) => gateway.resendCode(pending),
    [gateway],
  );

  const verifyCode = useCallback(
    async (pending: PendingCode, code: string) => {
      await gateway.verifyCode(pending, code);
      await refresh();
    },
    [gateway, refresh],
  );

  const requestPasswordReset = useCallback(
    (identifier: string) => gateway.requestPasswordReset(identifier),
    [gateway],
  );

  const confirmPasswordReset = useCallback(
    async (newPassword: string) => {
      await gateway.confirmPasswordReset(newPassword);
      await refresh();
    },
    [gateway, refresh],
  );

  const updateNotificationSoundsEnabled = useCallback((enabled: boolean) => {
    if (actualProfile.current) actualProfile.current = { ...actualProfile.current, profile: { ...actualProfile.current.profile, notificationSoundsEnabled: enabled } };
    setState((current) => current.status === 'authenticated'
      ? {
          ...current,
          profile: {
            ...current.profile,
            profile: { ...current.profile.profile, notificationSoundsEnabled: enabled },
          },
        }
      : current);
  }, []);

  const updateNotificationEmailsEnabled = useCallback((enabled: boolean) => {
    if (actualProfile.current) actualProfile.current = { ...actualProfile.current, profile: { ...actualProfile.current.profile, notificationEmailsEnabled: enabled } };
    setState((current) => current.status === 'authenticated'
      ? {
          ...current,
          profile: {
            ...current.profile,
            profile: { ...current.profile.profile, notificationEmailsEnabled: enabled },
          },
        }
      : current);
  }, []);

  const updateNotificationToastsEnabled = useCallback((enabled: boolean) => {
    if (actualProfile.current) actualProfile.current = { ...actualProfile.current, profile: { ...actualProfile.current.profile, notificationToastsEnabled: enabled } };
    setState((current) => current.status === 'authenticated'
      ? {
          ...current,
          profile: {
            ...current.profile,
            profile: { ...current.profile.profile, notificationToastsEnabled: enabled },
          },
        }
      : current);
  }, []);

  const setAccessView = useCallback((requested: Exclude<AccessView, null>) => {
    const profile = actualProfile.current;
    if (!profile) return;
    const view = allowedAccessView(profile, requested);
    if (!view) return;
    try { localStorage.setItem(ACCESS_VIEW_STORAGE_KEY, view); } catch { /* La vista sigue vigente en esta pestaña. */ }
    setStoredAccessView(view);
    setState({ status: 'authenticated', profile: profileForAccessView(profile, view) });
  }, []);

  const signOut = useCallback(async () => {
    await gateway.signOut();
    try { localStorage.removeItem(ACCESS_VIEW_STORAGE_KEY); } catch { /* El cierre de sesión continúa. */ }
    actualProfile.current = null;
    setStoredAccessView(null);
    setRealAccessLevel('user');
    setState({ status: 'anonymous' });
    track('logout');
  }, [gateway]);

  const value = useMemo(
    () => ({
      state,
      refresh,
      signIn,
      signInWithPassword,
      register,
      resendCode,
      verifyCode,
      requestPasswordReset,
      confirmPasswordReset,
      updateNotificationSoundsEnabled,
      updateNotificationEmailsEnabled,
      updateNotificationToastsEnabled,
      actualAccessLevel: realAccessLevel,
      accessView,
      setAccessView,
      signOut,
    }),
    [
      state,
      refresh,
      signIn,
      signInWithPassword,
      register,
      resendCode,
      verifyCode,
      requestPasswordReset,
      confirmPasswordReset,
      updateNotificationSoundsEnabled,
      updateNotificationEmailsEnabled,
      updateNotificationToastsEnabled,
      realAccessLevel,
      accessView,
      setAccessView,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession debe utilizarse dentro de SessionProvider.');
  return context;
}
